import {Component, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NgbModal, NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {ThaitracesReqLogService} from '../thaitraces-req-log.service';
import {RequestThaitracesPageData} from '../../../../../generated-model/models';
import {PageResponse} from '../../../../shared/commons/page-response';
import {ThaiDateTimePipe} from '../../../../shared/commons/thai-datetime.pipe';
import {PageTitleComponent} from '../../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../../shared/components/pagesize-select/pagesize-select.component';
import {ThaitracesReqLogAppDetailModalComponent} from '../app-detail-modal/thaitraces-req-log-app-detail-modal.component';

type StatusTone = 'success' | 'danger' | 'secondary';

@Component({
  selector: 'app-thaitraces-req-log-list',
  standalone: true,
  imports: [FormsModule, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent],
  templateUrl: './thaitraces-req-log-list.component.html',
  styleUrl: './thaitraces-req-log-list.component.scss'
})
export class ThaitracesReqLogListComponent implements OnInit {
  private readonly service = inject(ThaitracesReqLogService);
  private readonly modalService = inject(NgbModal);

  protected term = signal<string>('');
  protected requestType = signal<string>('');
  protected requestStatus = signal<string>('');
  protected pageData = signal<PageResponse<RequestThaitracesPageData>>({
    page: {size: 20, number: 0}
  } as PageResponse<RequestThaitracesPageData>);

  protected selectedIds = signal<Set<number>>(new Set());
  protected isAllSelected = signal<boolean>(false);

  ngOnInit(): void {
    this.search();
  }

  search(page: number = 1): void {
    const params: Record<string, string | number> = {
      page: page - 1,
      size: this.pageData().page.size,
      sort: 'timeRequest,desc'
    };
    if (this.term().trim()) {
      params['term'] = this.term().trim();
    }
    if (this.requestType()) {
      params['requestType'] = this.requestType();
    }
    if (this.requestStatus()) {
      params['requestStatus'] = this.requestStatus();
    }
    this.service.search(params).subscribe({
      next: (res) => {
        this.pageData.set(res);
        this.selectedIds.set(new Set());
        this.isAllSelected.set(false);
      },
      error: (err) => console.error(err)
    });
  }

  reset(): void {
    this.term.set('');
    this.requestType.set('');
    this.requestStatus.set('');
    this.search();
  }

  toggleSelectAll(): void {
    const ids = (this.pageData().content ?? []).map(r => r.id!).filter(id => id != null);
    if (this.isAllSelected()) {
      this.selectedIds.set(new Set());
      this.isAllSelected.set(false);
    } else {
      this.selectedIds.set(new Set(ids));
      this.isAllSelected.set(true);
    }
  }

  toggleSelectItem(id: number): void {
    const current = new Set(this.selectedIds());
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    this.selectedIds.set(current);
    const allIds = (this.pageData().content ?? []).map(r => r.id!).filter(id => id != null);
    this.isAllSelected.set(allIds.length > 0 && allIds.every(i => current.has(i)));
  }

  isItemSelected(id: number | undefined): boolean {
    return id != null && this.selectedIds().has(id);
  }

  openAppDetail(token: string): void {
    const ref = this.modalService.open(ThaitracesReqLogAppDetailModalComponent, {
      size: 'xl',
      scrollable: true,
      centered: true
    });
    ref.componentInstance.token = token;
  }

  protected statusTone(code: number | undefined): StatusTone {
    if (code === 200) {
      return 'success';
    }
    if (code === 500) {
      return 'danger';
    }
    return 'secondary';
  }

  protected statusText(code: number | undefined): string {
    if (code === 200) {
      return '200 : Ok';
    }
    if (code === 500) {
      return '500 : Error';
    }
    return code?.toString() ?? 'n/a';
  }

  // TODO: trigger fetch not implemented
  // triggerFetch(): void {
  //   this.service.triggerFetch().subscribe({ next: () => this.search() });
  // }
}
